import type { ResourceKind, ResourceView, Vec2 } from "@cc/protocol";
import type { RenderState } from "@cc/engine";

/**
 * Which information the viewer is currently seeing.
 *
 * `spectator` is the omniscient camera. `0` and `1` show the arena through one
 * cricket's eyes, which is the whole point of the game: the audience knows where
 * the trap is and the cricket does not, and being able to *see* that gap is what
 * makes an otherwise inexplicable decision legible.
 */
export type VisionMode = "spectator" | 0 | 1;

export interface VisionView {
  /** Resources this cricket is currently aware of, keyed by id. */
  readonly visible: ReadonlyMap<string, ResourceView>;
  /** Where this cricket is looking from. */
  readonly from: Vec2;
  readonly visionRadius: number;
}

/**
 * Recomputes one cricket's information set from a render snapshot.
 *
 * Deliberately a **pure function of the snapshot**, not a call into
 * `Simulation.observe()`. That method is not side-effect free — it writes
 * `self.memory`, `seenOpponent` and `lastOpponentPosition` — so calling it to
 * draw a frame would feed the agent sightings it never actually made and change
 * the match. Reconstructing the view here also means vision mode costs nothing
 * per frame.
 *
 * Two honest limitations, both stated rather than hidden:
 *
 *  1. `isTrapKnown` must be supplied by the caller with **per-cricket** trap
 *     knowledge. It cannot be read off the snapshot: the snapshot's `revealed`
 *     flag is global, set when *either* cricket identifies a trap, whereas
 *     `knownTraps` in the engine is per-cricket. Using the snapshot flag here
 *     would show A the trap that only B scouted — exactly the asymmetry leak
 *     this view exists to expose. The client tracks the real per-cricket sets
 *     from `trap:detected` events.
 *  2. Stale memory is not reproduced. This is *current* line of sight, not the
 *     cricket's full information set, which also includes resources it saw
 *     earlier and still remembers. The "Why?" panel answers that question from
 *     the real recorded observation instead.
 */
export function computeVision(
  state: RenderState,
  mode: VisionMode,
  visionRadius: number,
  isTrapKnown: (resourceId: string) => boolean,
): VisionView | null {
  if (mode === "spectator") return null;

  const cricket = state.crickets.find((c) => c.slot === mode) ?? state.crickets[mode];
  if (!cricket) return null;

  const visible = new Map<string, ResourceView>();

  for (const r of state.resources) {
    const d = Math.hypot(
      r.position[0] - cricket.position[0],
      r.position[1] - cricket.position[1],
    );
    if (d > visionRadius) continue;

    // A trap reads as a trap only once *this* cricket has identified it. Until
    // then it is indistinguishable from a crystal, which is what makes the bait
    // work. `isTrapKnown` is the per-cricket truth, not the global flag.
    const identified = r.kind === "trap" && isTrapKnown(r.id);
    visible.set(r.id, {
      id: r.id,
      kind: identified ? "trap" : (r.kind as ResourceKind),
      position: r.position,
      value: r.value,
      identifiedAsTrap: identified,
      inRange: false,
      distance: d,
    });
  }

  return { visible, from: cricket.position, visionRadius };
}

/** Is a resource inside the given view? Spectator vision sees everything. */
export function inVision(view: VisionView | null, resourceId: string): boolean {
  return view === null || view.visible.has(resourceId);
}

/** Can the viewing cricket currently see a point (usually the opponent)? */
export function pointInVision(view: VisionView | null, point: Vec2): boolean {
  if (view === null) return true;
  return (
    Math.hypot(point[0] - view.from[0], point[1] - view.from[1]) <= view.visionRadius
  );
}
